/* eslint-disable no-console */
import { canUseRepeatWrapForTexture, getWebGLCapabilities } from '../../render-pipelines/webgl-support';
import { ResolvedSamplerBinding, SamplerBindingSpec } from './shader-contract';

/** Pass `gl.VERTEX_SHADER` or `gl.FRAGMENT_SHADER`. */
export type GlShaderType = GLenum;

/** Pass `gl.CLAMP_TO_EDGE` or `gl.REPEAT`. */
export type TextureWrapMode = GLenum;

function getShaderTypeLabel(gl: WebGLRenderingContext, shaderType: GlShaderType): string {
  if (shaderType === gl.VERTEX_SHADER) {
    return 'vertex';
  }

  if (shaderType === gl.FRAGMENT_SHADER) {
    return 'fragment';
  }
  return `type-${shaderType}`;
}

export function CreateGlShader(gl: WebGLRenderingContext, shaderString: string, shaderType: GlShaderType): WebGLShader {
  const shader = gl.createShader(shaderType);
  if (!shader) {
    throw new Error(`PipelineGlUtils::CreateGlShader: GL context active <${!gl.isContextLost()}>. ` +
      `Failed to create shader of type <${shaderType}>`);
  }

  gl.shaderSource(shader, shaderString);
  gl.compileShader(shader);

  const compileStatus = gl.getShaderParameter(shader, gl.COMPILE_STATUS);
  if (!compileStatus) {
    const shaderTypeLabel = getShaderTypeLabel(gl, shaderType);
    const infoLog = gl.getShaderInfoLog(shader) || '(no info log)';
    gl.deleteShader(shader);
    throw new Error(
      `PipelineGlUtils::CreateGlShader: ${shaderTypeLabel} shader compile failed: ${infoLog}`
    );
  }

  return shader;
}

export function CreateShaderProgram(gl: WebGLRenderingContext, vertShaderStr: string, fragShaderStr: string): WebGLProgram {
  const vertShader = CreateGlShader(gl, vertShaderStr, gl.VERTEX_SHADER);
  const fragShader = CreateGlShader(gl, fragShaderStr, gl.FRAGMENT_SHADER);

  const shaderProgram = gl.createProgram();

  if (!shaderProgram) {
    gl.deleteShader(vertShader);
    gl.deleteShader(fragShader);
    throw new Error(`PipelineGlUtils::CreateShaderProgram: GL context active <${!gl.isContextLost()}>. Failed to create gl shader program.`);
  }

  gl.attachShader(shaderProgram, vertShader);
  gl.attachShader(shaderProgram, fragShader);
  gl.linkProgram(shaderProgram);

  const linkStatus = gl.getProgramParameter(shaderProgram, gl.LINK_STATUS);
  if (!linkStatus) {
    const programInfoLog = gl.getProgramInfoLog(shaderProgram) || '(no info log)';
    const vertInfoLog = gl.getShaderInfoLog(vertShader) || '(no info log)';
    const fragInfoLog = gl.getShaderInfoLog(fragShader) || '(no info log)';
    gl.deleteProgram(shaderProgram);
    gl.deleteShader(vertShader);
    gl.deleteShader(fragShader);
    throw new Error(
      `PipelineGlUtils::CreateShaderProgram: shader link failed.\n` +
      `Program info log: ${programInfoLog}\n` +
      `Vertex shader info log: ${vertInfoLog}\n` +
      `Fragment shader info log: ${fragInfoLog}\n` +
      `Vertex shader source:\n${vertShaderStr}\n` +
      `Fragment shader source:\n${fragShaderStr}`
    );
  }

  gl.detachShader(shaderProgram, vertShader);
  gl.detachShader(shaderProgram, fragShader);
  gl.deleteShader(vertShader);
  gl.deleteShader(fragShader);

  return shaderProgram;
}

export function assertGlNoError(gl: WebGLRenderingContext, operation: string): void {
  const errors: number[] = [];
  let error = gl.getError();
  while (error !== gl.NO_ERROR) {
    errors.push(error);
    error = gl.getError();
  }

  if (errors.length > 0) {
    throw new Error(`PipelineGlUtils: WebGL error(s) <${errors.join(', ')}> after ${operation}.`);
  }
}

export function EnableVertexAttributeArray(
  gl: WebGLRenderingContext,
  shaderProgram: WebGLProgram,
  shaderAttrName: string,
  pipelineName = 'unknown'
): number {
  const attrLoc = gl.getAttribLocation(shaderProgram, shaderAttrName);
  if (attrLoc < 0) {
    throw new Error(
      `PipelineGlUtils::EnableVertexAttributeArray: Could not find attribute <${shaderAttrName}> ` +
      `in pipeline <${pipelineName}>.`
    );
  }

  gl.enableVertexAttribArray(attrLoc);
  return attrLoc;
}

export function GetActiveUniformNames(gl: WebGLRenderingContext, shaderProgram: WebGLProgram): string[] {
  const names: string[] = [];
  const uniformCount = gl.getProgramParameter(shaderProgram, gl.ACTIVE_UNIFORMS) as number;

  for (let i = 0; i < uniformCount; i++) {
    const info = gl.getActiveUniform(shaderProgram, i);
    if (info) {
      names.push(info.name);
    }
  }

  return names;
}

/**
 * Use to debug all available uniforms of given program.
 */
export function GetUniformInfo(gl: WebGLRenderingContext, shaderProgram: WebGLProgram): void {
  const uniformCount = gl.getProgramParameter(shaderProgram, gl.ACTIVE_UNIFORMS) as number;

  for (let i = 0; i < uniformCount; i++) {
    const info = gl.getActiveUniform(shaderProgram, i);
    console.log(`Uniform index <${i}>:`, info);
  }
}

function resolveUniformLocation(
  gl: WebGLRenderingContext,
  shaderProgram: WebGLProgram,
  candidates: string[]
): WebGLUniformLocation | null {
  for (const candidate of candidates) {
    const location = gl.getUniformLocation(shaderProgram, candidate);
    if (location !== null) {
      return location;
    }
  }

  return null;
}

function throwUniformNotFound(
  methodName: string,
  gl: WebGLRenderingContext,
  shaderProgram: WebGLProgram,
  shaderUniformName: string,
  pipelineName: string
): never {
  const activeUniforms = GetActiveUniformNames(gl, shaderProgram);
  throw new Error(
    `PipelineGlUtils::${methodName}: Could not find shader uniform <${shaderUniformName}> ` +
    `in pipeline <${pipelineName}>. Active uniforms: <${activeUniforms.join(', ')}>`
  );
}

export function GetUniformLocation(
  gl: WebGLRenderingContext,
  shaderProgram: WebGLProgram,
  shaderUniformName: string,
  pipelineName = 'unknown'
): WebGLUniformLocation {
  const location = resolveUniformLocation(gl, shaderProgram, [shaderUniformName]);

  if (location !== null) {
    return location;
  }

  throwUniformNotFound('GetUniformLocation', gl, shaderProgram, shaderUniformName, pipelineName);
}

/**
 * Resolves array uniform locations with driver-specific fallbacks for the first element.
 */
export function GetArrayUniformLocation(
  gl: WebGLRenderingContext,
  shaderProgram: WebGLProgram,
  shaderUniformName: string,
  pipelineName = 'unknown'
): WebGLUniformLocation {
  const candidates = shaderUniformName.endsWith('[0]')
    ? [shaderUniformName, shaderUniformName.slice(0, -3)]
    : [shaderUniformName, `${shaderUniformName}[0]`];

  const location = resolveUniformLocation(gl, shaderProgram, candidates);

  if (location !== null) {
    return location;
  }

  throwUniformNotFound('GetArrayUniformLocation', gl, shaderProgram, shaderUniformName, pipelineName);
}

export function ResolveSamplerBindings(
  gl: WebGLRenderingContext,
  shaderProgram: WebGLProgram,
  bindings: SamplerBindingSpec[],
  pipelineName: string
): ResolvedSamplerBinding[] {
  return bindings.map((binding) => ({
    ...binding,
    location: GetUniformLocation(gl, shaderProgram, binding.name, pipelineName)
  }));
}

export function SetVertexAttribute(gl: WebGLRenderingContext, buffer: WebGLBuffer | null, attrLocation: number, itemSize: number) {
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.vertexAttribPointer(attrLocation, itemSize, gl.FLOAT, false, 0, 0);
}

function assertTextureDimensions(gl: WebGLRenderingContext, width: number, height: number): void {
  const { maxTextureSize, maxRenderbufferSize } = getWebGLCapabilities(gl);
  const withinLimits = width > 0
    && height > 0
    && width <= maxTextureSize
    && height <= maxTextureSize
    && width <= maxRenderbufferSize
    && height <= maxRenderbufferSize;

  if (!withinLimits) {
    throw new Error(
      `PipelineGlUtils: Texture dimensions <${width}x${height}> exceed device WebGL limits.`
    );
  }
}

function assertTextureWrapSupported(
  gl: WebGLRenderingContext,
  width: number,
  height: number,
  wrapMode: TextureWrapMode
): void {
  if (wrapMode === gl.REPEAT && !canUseRepeatWrapForTexture(gl, width, height)) {
    throw new Error(
      `PipelineGlUtils: gl.REPEAT requires power-of-two texture dimensions on WebGL 1 ` +
      `(got <${width}x${height}>).`
    );
  }
}

function configureTexture2D(
  gl: WebGLRenderingContext,
  wrapMode: TextureWrapMode,
  generateMipmap = false
): void {
  SetTextureWrap(gl, wrapMode);
  SetTextureMagFilter(gl);
  SetTextureMinFilter(gl, generateMipmap);
  UnBindTexture(gl);
}

export function CreateGlTextureByImageData(gl: WebGLRenderingContext, imgData: ImageData, wrapMode: TextureWrapMode): WebGLTexture {
  assertTextureDimensions(gl, imgData.width, imgData.height);
  assertTextureWrapSupported(gl, imgData.width, imgData.height, wrapMode);
  const glTexture = CreateGlTextureInternal(gl);

  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, imgData);
  assertGlNoError(gl, `texImage2D(image ${imgData.width}x${imgData.height})`);

  configureTexture2D(gl, wrapMode);

  return glTexture;
}

export function CreateGlTextureByPixels(gl: WebGLRenderingContext, width: number, height: number, pixels: ArrayBufferView | null): WebGLTexture {
  assertTextureDimensions(gl, width, height);
  const glTexture = CreateGlTextureInternal(gl);

  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
  assertGlNoError(gl, `texImage2D(pixels ${width}x${height})`);

  configureTexture2D(gl, gl.CLAMP_TO_EDGE);

  return glTexture;
}

function CreateGlTextureInternal(gl: WebGLRenderingContext): WebGLTexture {
  const glTexture = gl.createTexture();
  if (glTexture === null) {
    throw new Error(`PipelineGlUtils::CreateGlTextureInternal: Could not create empty gl texture`);
  }

  gl.bindTexture(gl.TEXTURE_2D, glTexture);

  return glTexture;
}

/**
 * ! WebGL 1 will ONLY accept power-of-two textures if gl.REPEAT needs to be used
 */
export function SetTextureWrap(gl: WebGLRenderingContext, wrapMode: TextureWrapMode) {
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, wrapMode);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, wrapMode);
}

export function SetTextureMagFilter(gl: WebGLRenderingContext) {
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
}

/**
 * ! WebGL 1 will ONLY accept power-of-two textures if gl.generateMipmap needs to be used
 */
export function SetTextureMinFilter(gl: WebGLRenderingContext, generateMipmap: boolean) {
  if (generateMipmap) {
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_NEAREST);
    gl.generateMipmap(gl.TEXTURE_2D);
  }
  else {
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  }
}

export function UnBindTexture(gl: WebGLRenderingContext) {
  gl.bindTexture(gl.TEXTURE_2D, null);
}

export function CreateTextureBoundFrameBuffer(gl: WebGLRenderingContext, targetTexture: WebGLTexture): WebGLFramebuffer {
  const frameBuffer = gl.createFramebuffer();
  if (frameBuffer === null) {
    throw new Error(`PipelineGlUtils::CreateTextureBoundFrameBuffer: Could not create frame buffer`);
  }

  gl.bindFramebuffer(gl.FRAMEBUFFER, frameBuffer);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, targetTexture, 0);

  const framebufferStatus = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
  if (framebufferStatus !== gl.FRAMEBUFFER_COMPLETE) {
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.deleteFramebuffer(frameBuffer);
    throw new Error(`PipelineGlUtils::CreateTextureBoundFrameBuffer: Framebuffer incomplete, status <${framebufferStatus}>`);
  }

  gl.bindFramebuffer(gl.FRAMEBUFFER, null);

  return frameBuffer;
}

export function UnBindFrameBuffer(gl: WebGLRenderingContext) {
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
}

export function SetTextureUniform(
  gl: WebGLRenderingContext,
  activeTexture: number,
  glTexture: WebGLTexture,
  location: WebGLUniformLocation | null,
  samplerUnit: number
) {
  if (location === null) {
    throw new Error('PipelineGlUtils::SetTextureUniform: uniform location is null.');
  }

  gl.activeTexture(activeTexture);
  gl.bindTexture(gl.TEXTURE_2D, glTexture);
  gl.uniform1i(location, samplerUnit);
}

export function DeleteTexture(gl: WebGLRenderingContext, texture: WebGLTexture | null | undefined): void {
  if (texture) {
    gl.deleteTexture(texture);
  }
}

export function DeleteFramebuffer(gl: WebGLRenderingContext, framebuffer: WebGLFramebuffer | null | undefined): void {
  if (framebuffer) {
    gl.deleteFramebuffer(framebuffer);
  }
}

export function DeleteTextures(gl: WebGLRenderingContext, textures: readonly WebGLTexture[]): void {
  for (const texture of textures) {
    DeleteTexture(gl, texture);
  }
}
